import { useState } from "react";
import { Layers, Globe2, Building2 } from "lucide-react";
import countries from "../../../../data/countries";
import { submitForm } from "../../../../api/submitForm";
import Honeypot from "../../../../components/honeypot";
import { useFormTiming } from "../../../../components/useFormTiming";
import { useTranslation } from "../../../../i18n/useTranslation";

const Invest = () => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="band-hero section text-center">
        <div className="eyebrow">{t("invest.eyebrow")}</div>
        <h1>{t("invest.heading")}</h1>
        <div className="max-w-xl mx-auto pt-3">
          <p className="mini">{t("invest.intro")}</p>
        </div>
      </div>

      <div className="band-a section-tight">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 px-5">
          <div className="card-ledger text-center">
            <Layers className="mx-auto" size={40} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
            <h4 className="pt-2">{t("invest.portfolioTitle")}</h4>
            <p className="mini">{t("invest.portfolioBody")}</p>
          </div>
          <div className="card-ledger text-center">
            <Globe2 className="mx-auto" size={40} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
            <h4 className="pt-2">{t("invest.marketsTitle")}</h4>
            <p className="mini">{t("invest.marketsBody")}</p>
          </div>
          <div className="card-ledger text-center">
            <Building2 className="mx-auto" size={40} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
            <h4 className="pt-2">{t("invest.modelTitle")}</h4>
            <p className="mini">{t("invest.modelBody")}</p>
          </div>
        </div>
      </div>

      <div className="band-b section-tight">
        <div className="max-w-xl mx-auto text-center">
          <div className="eyebrow">{t("invest.getInTouchEyebrow")}</div>
          <h2>{t("invest.heading2")}</h2>
          <p className="mini pt-2">{t("invest.subIntro")}</p>
        </div>

        <div className="max-w-md mx-auto px-5 pt-6">
          <InvestForm />
        </div>
      </div>
    </div>
  );
};

const InvestForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", investorType: "", country: "", message: "" });
  const [hp, setHp] = useState("");
  const startedAt = useFormTiming();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const result = await submitForm(
      "investor",
      "global",
      {
        name: form.name,
        email: form.email,
        investorType: form.investorType,
        country: form.country,
        message: form.message,
      },
      { hp, startedAt }
    );
    if (result.ok) {
      setStatus("sent");
    } else {
      setStatus("error");
      setError(result.error);
    }
  };

  if (status === "sent") {
    return <p className="mini text-center">{t("invest.sentMessage")}</p>;
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
      <input className="field" placeholder={t("invest.namePlaceholder")} required value={form.name} onChange={update("name")} />
      <input type="email" className="field" placeholder={t("invest.emailPlaceholder")} required value={form.email} onChange={update("email")} />
      <select className="field" required value={form.investorType} onChange={update("investorType")}>
        <option value="" disabled>{t("invest.investorTypePlaceholder")}</option>
        <option value="citizens">{t("invest.citizens")}</option>
        <option value="smb">{t("invest.smb")}</option>
        <option value="corporations">{t("invest.corporations")}</option>
        <option value="governments">{t("invest.governments")}</option>
      </select>
      <select className="field" required value={form.country} onChange={update("country")}>
        <option value="" disabled>{t("invest.countryPlaceholder")}</option>
        {countries.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <textarea className="field" placeholder={t("invest.messagePlaceholder")} rows="4" required value={form.message} onChange={update("message")} />
      <button type="submit" className="btn-brass self-center mt-1" disabled={status === "sending"}>
        {status === "sending" ? t("invest.sendingBtn") : t("invest.sendBtn")}
      </button>
      {status === "error" && <p className="mini text-center" style={{ color: "#c0392b" }}>{error}</p>}
    </form>
  );
};

export default Invest;
